import * as React from 'react';
import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, Checkbox, FormControlLabel } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiRequest, normalizePhoneCL, isValidRutCL } from 'GlobalFunctions';
import AutocompleteField from 'elements/AutocompleteField';
import ImageUploader from 'elements/ImageUploader';
import { useAuth } from 'contexts/AuthContext';
import { useSnackbar } from 'notistack';

const missingFieldLabels: Record<string, string> = {
    image: 'Foto de perfil',
    rut: 'RUT',
    locations: 'Comunas de atención',
    whatsapp: 'WhatsApp',
    home_comuna_id: 'Comuna de residencia',
};

/**
 * Modal bloqueante que obliga a un kinesiólogo con perfil incompleto, y/o que
 * todavía no acepta los nuevos términos de comisión por derivación ($10.000),
 * a resolver ambas cosas antes de poder usar el resto de la cuenta.
 * Se monta una vez a nivel de app (ver index.tsx).
 */
export default function ProfileCompletionGate() {
    const { isAuthenticated, role_id } = useAuth();
    const { enqueueSnackbar } = useSnackbar();

    const [checked, setChecked] = useState(false);
    const [missing, setMissing] = useState<string[]>([]);
    const [formValues, setFormValues] = useState<any>(null);
    const [files, setFiles] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [rutError, setRutError] = useState(false);
    const [whatsappError, setWhatsappError] = useState(false);
    const [commissionTerms, setCommissionTerms] = useState('');
    const [commissionAccepted, setCommissionAccepted] = useState(false);

    const loadDetails = () => {
        ApiRequest({
            url: '/users/details',
            method: 'GET',
            setResponse: (response: any) => {
                setChecked(true);
                if (response && response.role_id === 2) {
                    setFormValues(response);
                    setMissing(response.missing_profile_fields || []);
                } else {
                    setFormValues(null);
                    setMissing([]);
                }
            },
        });
    };

    useEffect(() => {
        if (isAuthenticated && role_id === 2) {
            loadDetails();
            ApiRequest({
                url: '/settings/details',
                method: 'GET',
                setResponse: (response: any) => setCommissionTerms(response?.seo?.preferente_terms || ''),
            });
        } else {
            setChecked(true);
            setFormValues(null);
            setMissing([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, role_id]);

    const needsCommission = !!formValues && formValues.commission_consent !== true;

    if (!checked || !formValues || (missing.length === 0 && !needsCommission)) return null;

    const validateWhatsApp = (whatsapp: string) => /^\d{9}$/.test(whatsapp);

    const canSubmit = missing.every((field) => {
        if (field === 'rut') return isValidRutCL(formValues.rut);
        if (field === 'locations') return Array.isArray(formValues.locations) && formValues.locations.length > 0;
        if (field === 'home_comuna_id') return !!formValues.home_comuna_id;
        if (field === 'whatsapp') return validateWhatsApp(formValues.whatsapp || '');
        if (field === 'image') return !!files || !!formValues.image;
        return true;
    }) && (!needsCommission || commissionAccepted);

    const submit = async () => {
        setLoading(true);

        if (missing.length > 0) {
            const body = new FormData();
            body.append('formValues', JSON.stringify(formValues));
            if (files) body.append('image', files);

            const updateOk = await new Promise<boolean>((resolve) => {
                ApiRequest({
                    url: '/users/updateAccount',
                    method: 'POST',
                    formData: true,
                    body,
                    setResponse: (response: any) => {
                        if (response?.status !== 'success') {
                            enqueueSnackbar(response?.message || 'Error al guardar', { variant: 'error' });
                        }
                        resolve(response?.status === 'success');
                    },
                });
            });
            if (!updateOk) {
                setLoading(false);
                return;
            }
        }

        if (needsCommission && commissionAccepted) {
            const commissionOk = await new Promise<boolean>((resolve) => {
                ApiRequest({
                    url: '/kinesiologists/acceptCommission',
                    method: 'POST',
                    body: {},
                    setResponse: (response: any) => {
                        if (response?.status !== 'success') {
                            enqueueSnackbar(response?.message || 'No se pudo registrar la aceptación de comisión', { variant: 'error' });
                        }
                        resolve(response?.status === 'success');
                    },
                });
            });
            if (!commissionOk) {
                setLoading(false);
                return;
            }
        }

        setLoading(false);
        enqueueSnackbar('Perfil actualizado correctamente', { variant: 'success' });
        loadDetails();
    };

    return (
        <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown>
            <DialogTitle sx={{ fontWeight: 700 }}>Completa tu perfil para continuar</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {missing.length > 0 && (
                    <p style={{ margin: 0, color: '#475569', fontSize: 14 }}>
                        Para seguir usando tu cuenta y aparecer en el buscador de pacientes, necesitamos que completes: <b>{missing.map(f => missingFieldLabels[f] || f).join(', ')}</b>.
                    </p>
                )}

                {missing.includes('image') && (
                    <ImageUploader
                        className="user_image"
                        image={formValues.image}
                        setImage={(newImage: any) => setFormValues({ ...formValues, image: newImage })}
                        setFiles={setFiles}
                    />
                )}

                {missing.includes('rut') && (
                    <TextField
                        label="RUT"
                        value={formValues.rut}
                        onChange={(e) => {
                            setFormValues({ ...formValues, rut: e.target.value });
                            setRutError(e.target.value !== '' && !isValidRutCL(e.target.value));
                        }}
                        error={rutError}
                        helperText={rutError ? 'RUT inválido' : ''}
                        variant="outlined"
                        fullWidth
                    />
                )}

                {missing.includes('locations') && (
                    <AutocompleteField
                        label="Comunas de atención"
                        endpoint="/locations/comunas/list"
                        renderValues={["label", "region"]}
                        multiple={true}
                        value={formValues.locations}
                        onChange={(data: any) => setFormValues({ ...formValues, locations: data ? data : [] })}
                    />
                )}

                {missing.includes('home_comuna_id') && (
                    <AutocompleteField
                        label="Comuna de residencia"
                        endpoint="/locations/comunas/list"
                        renderValues={["label", "region"]}
                        value={formValues.home_comuna_id}
                        onChange={(data: any) => setFormValues({ ...formValues, home_comuna_id: data ? data.comuna_id : '' })}
                    />
                )}

                {missing.includes('whatsapp') && (
                    <TextField
                        label="WhatsApp"
                        value={formValues.whatsapp}
                        onChange={(e) => {
                            const normalized = normalizePhoneCL(e.target.value);
                            setFormValues({ ...formValues, whatsapp: normalized });
                            setWhatsappError(!validateWhatsApp(normalized) && normalized !== '');
                        }}
                        error={whatsappError}
                        helperText={whatsappError ? 'WhatsApp inválido (9 dígitos)' : ''}
                        variant="outlined"
                        fullWidth
                    />
                )}

                {needsCommission && (
                    <div style={{ padding: '14px 16px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '6px', color: '#92400e' }}>Condiciones de Cobro por Derivación ($10.000)</div>
                        <div style={{ fontSize: '13px', color: '#78350f', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: commissionTerms }} />
                        <FormControlLabel
                            control={<Checkbox checked={commissionAccepted} onChange={(e) => setCommissionAccepted(e.target.checked)} />}
                            label="Acepto estas condiciones"
                            style={{ marginTop: '8px' }}
                        />
                    </div>
                )}

                <LoadingButton
                    fullWidth
                    variant="contained"
                    disabled={!canSubmit || loading}
                    loading={loading}
                    onClick={submit}
                    sx={{ padding: '12px 0', fontWeight: 700, marginTop: '8px' }}
                >
                    Guardar y continuar
                </LoadingButton>
            </DialogContent>
        </Dialog>
    );
}
